import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { Save, ArrowLeft } from 'lucide-react';
import type { Company, CompanyCreate } from '@kss/api';
import { companyService } from '@kss/api';
import { validateEmail, validateGst, validateMobile, validatePan } from '../../utils/formatValidators';
import AddressFields from '../../components/AddressFields';
import ImagePicker from '../../components/ImagePicker';
import { persistImage } from '../../utils/persistImage';

/**
 * Company form — fields rewritten 2026-05-02 to match the live API.
 * The legacy form sent `code/pan/address/logo` (which the server
 * silently ignored) — canonical field names are
 * `legalName, panNumber, addressLine1/2, city, state, postalCode,
 * country, logoUrl, email, phone`.
 */

type FormState = {
  name: string;
  legalName: string;
  gstNumber: string;
  panNumber: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  logoUrl: string;
  /* Bank + UPI — surfaced on the printable invoice (see
     `apps/panel/src/pages/invoice/InvoicePreview.tsx`). All optional. */
  bankName: string;
  bankAccountNumber: string;
  bankIfsc: string;
  bankBranch: string;
  upiId: string;
  upiQrUrl: string;
  isActive: boolean;
};
const emptyForm: FormState = {
  name: '',
  legalName: '',
  gstNumber: '',
  panNumber: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  // Live API has `country varchar(5)`. Save the ITU-T E.164 calling
  // code ("91" for India). See COUNTRIES in indiaLocations.ts.
  country: '91',
  logoUrl: '',
  bankName: '',
  bankAccountNumber: '',
  bankIfsc: '',
  bankBranch: '',
  upiId: '',
  upiQrUrl: '',
  isActive: true,
};

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function NewCompany() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const isEdit = !!id && id !== 'new';

  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let c = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setErr(null);
        try {
          const r = await companyService.get(id);
          if (c) return;
          setForm({
            name: r.name ?? '',
            legalName: r.legalName ?? '',
            gstNumber: r.gstNumber ?? '',
            panNumber: r.panNumber ?? r.pan ?? '',
            email: r.email ?? '',
            phone: r.phone ?? '',
            addressLine1: r.addressLine1 ?? r.address ?? '',
            addressLine2: r.addressLine2 ?? '',
            city: r.city ?? '',
            state: r.state ?? '',
            postalCode: r.postalCode ?? '',
            country: r.country ?? '91',
            logoUrl: r.logoUrl ?? r.logo ?? '',
            bankName: r.bankName ?? '',
            bankAccountNumber: r.bankAccountNumber ?? '',
            bankIfsc: r.bankIfsc ?? '',
            bankBranch: r.bankBranch ?? '',
            upiId: r.upiId ?? '',
            upiQrUrl: r.upiQrUrl ?? '',
            isActive: !!r.isActive,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!c) setLoading(false);
        }
      }
    }
    run();
    return () => {
      c = true;
    };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const blurValidate = <K extends keyof FormState>(
    key: K,
    label: string,
    validator: (v: string) => string | null,
  ) => (e: React.FocusEvent<HTMLInputElement>) => {
    const errMsg = validator(e.currentTarget.value);
    if (errMsg) {
      toast.error(`Invalid ${label}`, errMsg);
      set(key, '' as FormState[K]);
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Fix errors', 'Name is required');
      return;
    }
    // Run format validators and blank invalid values before sending.
    const checks: [keyof FormState, (v: string) => string | null, string][] = [
      ['email', validateEmail, 'email'],
      ['phone', validateMobile, 'mobile'],
      ['gstNumber', validateGst, 'GST'],
      ['panNumber', validatePan, 'PAN'],
    ];
    for (const [k, fn, label] of checks) {
      const v = form[k] as string;
      if (v && fn(v)) {
        toast.error(`Invalid ${label}`, fn(v) ?? 'Format check failed');
        return;
      }
    }

    setSaving(true);
    try {
      const body: Company = {
        companyId: isEdit ? id : undefined,
        name: form.name,
        legalName: form.legalName || undefined,
        gstNumber: form.gstNumber ? form.gstNumber.toUpperCase() : undefined,
        panNumber: form.panNumber ? form.panNumber.toUpperCase() : undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        addressLine1: form.addressLine1 || undefined,
        addressLine2: form.addressLine2 || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        country: form.country || undefined,
        logoUrl: await persistImage(form.logoUrl, 'company'),
        bankName: form.bankName || undefined,
        bankAccountNumber: form.bankAccountNumber || undefined,
        bankIfsc: form.bankIfsc ? form.bankIfsc.toUpperCase() : undefined,
        bankBranch: form.bankBranch || undefined,
        upiId: form.upiId || undefined,
        upiQrUrl: await persistImage(form.upiQrUrl, 'company'),
        isActive: form.isActive,
      };
      if (isEdit && id) {
        await companyService.update([body]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        const { companyId: _o, ...create } = body;
        await companyService.create([create as CompanyCreate]);
        toast.success('Saved', `${form.name} added`);
      }
      navigate('/panel/companies');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/companies')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit company' : 'New company'}
          </h2>
        </div>
        {loading ? (
          <div className="text-sm text-content-muted">Loading…</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">
                Name <span className="text-danger">*</span>
              </span>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Acme Traders" className={inputCls} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Legal name</span>
              <input value={form.legalName} onChange={(e) => set('legalName', e.target.value)} placeholder="Acme Traders Private Limited" className={inputCls} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">GST number</span>
              <input
                value={form.gstNumber}
                onChange={(e) => set('gstNumber', e.target.value.toUpperCase())}
                onBlur={blurValidate('gstNumber', 'GST', validateGst)}
                placeholder="27AAAPL1234C1ZA"
                maxLength={15}
                className={`${inputCls} font-mono`}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">PAN</span>
              <input
                value={form.panNumber}
                onChange={(e) => set('panNumber', e.target.value.toUpperCase())}
                onBlur={blurValidate('panNumber', 'PAN', validatePan)}
                placeholder="AAAPL1234C"
                maxLength={10}
                className={`${inputCls} font-mono`}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                onBlur={blurValidate('email', 'email', validateEmail)}
                placeholder="hello@acme.com"
                maxLength={254}
                className={inputCls}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Phone</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                onBlur={blurValidate('phone', 'mobile', validateMobile)}
                placeholder="+91 9876543210"
                maxLength={15}
                className={inputCls}
              />
            </label>
            <div className="md:col-span-2">
              <ImagePicker
                label="Logo"
                value={form.logoUrl}
                onChange={(v) => set('logoUrl', v)}
                hint="Paste a URL or upload a file (≤ 200 KB)."
              />
            </div>
            {/* Shared address picker — pincode auto-fills city/state/country
                via api.postalpincode.in. */}
            <AddressFields
              addressLine1={form.addressLine1}
              setAddressLine1={(v) => set('addressLine1', v)}
              addressLine2={form.addressLine2}
              setAddressLine2={(v) => set('addressLine2', v)}
              showLine2
              pincode={form.postalCode}
              setPincode={(v) => set('postalCode', v)}
              city={form.city}
              setCity={(v) => set('city', v)}
              state={form.state}
              setState={(v) => set('state', v)}
              country={form.country}
              setCountry={(v) => set('country', v)}
            />
            {/* Bank + UPI block — surfaced on the printable invoice's
                "Billed By" panel (see InvoicePreview.tsx). All optional.
                IFSC auto-uppercases like GST/PAN. */}
            <div className="md:col-span-2 mt-2 border-t border-border pt-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                Bank &amp; UPI (shown on invoice)
              </h3>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Bank name</span>
              <input
                value={form.bankName}
                onChange={(e) => set('bankName', e.target.value)}
                placeholder="HDFC Bank"
                className={inputCls}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Account number</span>
              <input
                value={form.bankAccountNumber}
                onChange={(e) => set('bankAccountNumber', e.target.value)}
                placeholder="50100123456789"
                maxLength={20}
                className={`${inputCls} font-mono`}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">IFSC code</span>
              <input
                value={form.bankIfsc}
                onChange={(e) => set('bankIfsc', e.target.value.toUpperCase())}
                placeholder="HDFC0001234"
                maxLength={11}
                className={`${inputCls} font-mono`}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Branch</span>
              <input
                value={form.bankBranch}
                onChange={(e) => set('bankBranch', e.target.value)}
                placeholder="Andheri East, Mumbai"
                className={inputCls}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">UPI ID (VPA)</span>
              <input
                value={form.upiId}
                onChange={(e) => set('upiId', e.target.value)}
                placeholder="kristysoftware@okhdfcbank"
                className={inputCls}
              />
            </label>
            <div className="md:col-span-2">
              {/* Hosted QR PNG — optional. When blank, InvoicePreview
                  generates one client-side from `upiId`. */}
              <ImagePicker
                label="UPI QR code (optional)"
                value={form.upiQrUrl}
                onChange={(v) => set('upiQrUrl', v)}
                hint="Paste a URL or upload a PNG (≤ 200 KB). Leave blank to auto-generate from the UPI ID on invoices."
              />
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Status</span>
              <select
                value={form.isActive ? 'active' : 'inactive'}
                onChange={(e) => set('isActive', e.target.value === 'active')}
                className={selectCls}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </label>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate('/panel/companies')}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !form.name.trim()}>
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
